import { useState, useEffect } from 'react';
import { useLocation, Link, useParams } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet';
import { DynamicMain } from '@/components/layout/dynamic-main';
import { PageLayout } from '@/components/ui/page-layout';
import type { Customer, Proposal, LetterOfAuthority } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Users,
  FileText,
  PenTool,
  Send,
  Trash,
  Phone,
  Home,
  Calendar,
  Mail,
  Printer,
  ArrowLeft,
} from 'lucide-react';

export default function LoaDetailPage() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Debug logging on page load
  useEffect(() => {
    console.log(`LOA detail page loaded for ID: ${id}`);
  }, [id]);

  // Fetch LOA from public endpoint to ensure we can see it regardless of auth state
  const {
    data: loaData,
    isLoading: isLoadingLoa,
    isError: isLoaError,
  } = useQuery({
    queryKey: ['/api/public/letters-of-authority', Number(id)],
    queryFn: async () => {
      const response = await fetch(`/api/public/letters-of-authority/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch LOA data');
      }
      const data = await response.json();
      console.log('Fetched LOA data:', data);
      return data as LetterOfAuthority;
    },
    enabled: !!id,
  });

  // Get the LOA data if available
  const loa = loaData;

  // Fetch customer details if we have a customerId
  const { data: customer, isLoading: isLoadingCustomer } = useQuery({
    queryKey: loa?.customerId && loa.customerId > 0 ? ['/api/customers', loa.customerId] : [],
    queryFn: async () => {
      const response = await fetch(`/api/customers/${loa?.customerId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch customer data');
      }
      return (await response.json()) as Customer;
    },
    enabled: !!(loa?.customerId && loa.customerId > 0),
  });

  // Fetch proposal details if there's a proposalId
  const { data: proposal, isLoading: isLoadingProposal } = useQuery({
    queryKey: loa?.proposalId ? ['/api/proposals', loa.proposalId] : [],
    queryFn: async () => {
      const response = await fetch(`/api/proposals/${loa?.proposalId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch proposal data');
      }
      return (await response.json()) as Proposal;
    },
    enabled: !!loa?.proposalId,
  });

  // Send LOA mutation
  const sendLoaMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest({
        url: `/api/letters-of-authority/${id}/send`,
        method: 'POST',
        data: {},
      });
    },
    onSuccess: () => {
      toast({
        title: 'Letter of Authority Sent',
        description: 'The letter has been sent successfully to the customer.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/letters-of-authority', Number(id)] });
      queryClient.invalidateQueries({ queryKey: ['/api/public/letters-of-authority', Number(id)] });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to Send Letter',
        description: error.message || 'There was an error sending the letter.',
        variant: 'destructive',
      });
    },
  });

  // Delete LOA mutation
  const deleteLoaMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest({
        url: `/api/letters-of-authority/${id}`,
        method: 'DELETE',
        data: {},
      });
    },
    onSuccess: () => {
      toast({
        title: 'Letter of Authority Deleted',
        description: 'The letter has been deleted successfully.',
      });
      navigate('/loa');
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to Delete Letter',
        description: error.message || 'There was an error deleting the letter.',
        variant: 'destructive',
      });
    },
  });

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A';
    return format(new Date(date), 'dd MMM yyyy');
  };

  // Loading state
  if (
    isLoadingLoa ||
    (loa?.customerId && loa.customerId > 0 && isLoadingCustomer) ||
    (loa?.proposalId && isLoadingProposal)
  ) {
    return (
      <DynamicMain>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </DynamicMain>
    );
  }

  // Error state
  if (isLoaError || !loa) {
    return (
      <DynamicMain>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
            Letter of Authority Not Found
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            The letter of authority you're looking for could not be found or you don't have
            permission to view it.
          </p>
          <div className="mt-6">
            <Button variant="outline" onClick={() => navigate('/loa')}>
              Back to LOA List
            </Button>
          </div>
        </div>
      </DynamicMain>
    );
  }

  // Successful state with data
  return (
    <DynamicMain>
      <Helmet>
        <title>Letter of Authority | DomElec Solar</title>
      </Helmet>

      {loa && (
        <PageLayout
          title={`Letter of Authority #${loa.id}`}
          description="Manage customer energy supply authorization documents"
        >
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full h-8 w-8"
                    onClick={() => navigate('/loa')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-2 mt-1">
                  <Badge className="capitalize">
                    {loa.status === 'draft' && 'Draft'}
                    {loa.status === 'sent' && 'Sent for Signing'}
                    {loa.status === 'signed' && 'Signed'}
                    {loa.status === 'cancelled' && 'Cancelled'}
                  </Badge>
                  {loa.customerType && (
                    <Badge variant="outline" className="capitalize">
                      {loa.customerType}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
                  {loa.status === 'draft' && (
                    <>
                      <Link href={`/loa-form/${loa.id}`}>
                        <Button variant="outline" size="sm" className="gap-2">
                          <PenTool className="h-4 w-4" />
                          Edit
                        </Button>
                      </Link>

                      <Button
                        variant="domelec-blue"
                        size="sm"
                        className="gap-2"
                        onClick={() => sendLoaMutation.mutate()}
                        disabled={sendLoaMutation.isPending || !loa.customerEmail}
                      >
                        <Send className="h-4 w-4" />
                        {sendLoaMutation.isPending ? 'Sending...' : 'Send for Signature'}
                      </Button>
                    </>
                  )}

                  {loa.status === 'signed' && (
                    <>
                      <a
                        href={`/api/letters-of-authority/${loa.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" size="sm" className="gap-2">
                          <FileText className="h-4 w-4" />
                          Download PDF
                        </Button>
                      </a>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => {
                          const printWindow = window.open(
                            `/api/letters-of-authority/${loa.id}/pdf`,
                            '_blank'
                          );
                          if (printWindow) {
                            printWindow.addEventListener('load', () => {
                              printWindow.print();
                            });
                          }
                        }}
                      >
                        <Printer className="h-4 w-4" />
                        Print
                      </Button>
                    </>
                  )}

                  <Button
                    variant="destructive"
                    size="sm"
                    className="gap-2"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 slide-up">
              <Card className="card-modern">
                <CardHeader className="card-header">
                  <CardTitle className="card-title flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <Users className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
                    </div>
                    Customer
                  </CardTitle>
                  <CardDescription className="card-description">
                    Customer information
                  </CardDescription>
                </CardHeader>
                <CardContent className="card-content">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                      <div className="text-slate-800 dark:text-slate-200 font-medium">
                        {customer?.name ||
                          (loa.customerType === 'commercial' && loa.companyName
                            ? loa.companyName
                            : loa.authorizedPersonName) ||
                          'Unknown Customer'}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {/* Show email from customer record or from LOA */}
                      {(customer?.email || loa.customerEmail) && (
                        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                          <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Mail className="h-3.5 w-3.5 text-slate-500" />
                          </div>
                          <span>{customer?.email || loa.customerEmail}</span>
                        </div>
                      )}

                      {/* Show phone from customer record */}
                      {customer?.phone && (
                        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                          <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Phone className="h-3.5 w-3.5 text-slate-500" />
                          </div>
                          <span>{customer?.phone}</span>
                        </div>
                      )}

                      {/* Show address from customer record or from LOA */}
                      {(customer?.address || loa.companyAddress) && (
                        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                          <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Home className="h-3.5 w-3.5 text-slate-500" />
                          </div>
                          <span>{customer?.address || loa.companyAddress}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-modern">
                <CardHeader>
                  <CardTitle className="card-title flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-teal-500 dark:text-teal-400" />
                    </div>
                    Proposal
                  </CardTitle>
                  <CardDescription>Related proposal details</CardDescription>
                </CardHeader>
                <CardContent>
                  {proposal ? (
                    <div className="space-y-3">
                      <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                        <div className="text-slate-800 dark:text-slate-200 font-medium">
                          {proposal.title || 'Untitled Proposal'}
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          ID: {proposal.id}
                        </div>
                      </div>

                      <div className="text-sm">
                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 py-2">
                          <span className="text-slate-500 dark:text-slate-400">Date Created</span>
                          <span className="text-slate-800 dark:text-slate-200">
                            {formatDate(proposal.createdAt)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 py-2">
                          <span className="text-slate-500 dark:text-slate-400">Status</span>
                          <span className="text-slate-800 dark:text-slate-200 capitalize">
                            {proposal.status}
                          </span>
                        </div>
                      </div>

                      <Link href={`/proposal/${proposal.id}`}>
                        <Button variant="outline" size="sm" className="w-full gap-2">
                          <div className="flex items-center justify-between w-full">
                            <span>View Proposal</span>
                            <span>→</span>
                          </div>
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                      <div className="text-slate-800 dark:text-slate-200 font-medium">
                        No linked proposal
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="card-modern">
                <CardHeader>
                  <CardTitle className="card-title flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-orange-500 dark:text-orange-400" />
                    </div>
                    Status
                  </CardTitle>
                  <CardDescription>LOA progress tracking</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-sm">
                      <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 py-2">
                        <span className="text-slate-500 dark:text-slate-400">Created On</span>
                        <span className="text-slate-800 dark:text-slate-200 whitespace-nowrap">
                          {formatDate(loa.createdAt)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 py-2">
                        <span className="text-slate-500 dark:text-slate-400">Email Sent</span>
                        <span className="text-slate-800 dark:text-slate-200 whitespace-nowrap">
                          {loa.sentAt ? formatDate(loa.sentAt) : 'Not sent'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 py-2">
                        <span className="text-slate-500 dark:text-slate-400">Signing Status</span>
                        <span className="text-slate-800 dark:text-slate-200 capitalize whitespace-nowrap">
                          {loa.status === 'signed' && loa.dateOfSigning
                            ? `Signed on ${formatDate(loa.dateOfSigning)}`
                            : loa.status === 'sent'
                              ? 'Awaiting signature'
                              : 'Not yet sent for signing'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="card-modern mb-6 slide-up-delay">
              <CardHeader>
                <CardTitle className="card-title">Letter of Authority Details</CardTitle>
                <CardDescription>
                  Authorization information for DomElec Electrical & Solar Ltd
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-2">Authorized Person</h3>
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                            Name
                          </div>
                          <div className="text-slate-800 dark:text-slate-200">
                            {loa.authorizedPersonName}
                          </div>
                        </div>
                        {loa.customerType === 'commercial' && (
                          <div>
                            <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                              Title/Position
                            </div>
                            <div className="text-slate-800 dark:text-slate-200">
                              {loa.authorizedPersonTitle}
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                            Email
                          </div>
                          <div className="text-slate-800 dark:text-slate-200">
                            {loa.customerEmail}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {loa.customerType === 'commercial' && loa.companyName && (
                    <div>
                      <h3 className="text-lg font-medium mb-2">Company Information</h3>
                      <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                              Company Name
                            </div>
                            <div className="text-slate-800 dark:text-slate-200">
                              {loa.companyName}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                              Address
                            </div>
                            <div className="text-slate-800 dark:text-slate-200 whitespace-pre-line">
                              {loa.companyAddress}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-medium mb-2">Authorisation Scope</h3>
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                      <div className="text-slate-800 dark:text-slate-200">
                        <p className="mb-2">
                          This letter authorises DomElec Electrical & Solar Ltd to:
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>
                            Act on behalf of the{' '}
                            {loa.customerType === 'commercial' ? 'company' : 'homeowner'} in matters
                            relating to energy supply.
                          </li>
                          <li>
                            Liaise with electricity suppliers and DNO regarding solar energy
                            installations.
                          </li>
                          <li>Submit G98/G99 applications with the network operator.</li>
                          {loa.customerType === 'commercial' && (
                            <li>
                              Represent the company in all correspondence related to the commercial
                              solar installation.
                            </li>
                          )}
                          {loa.customerType === 'residential' && (
                            <li>
                              Access meter points and account information for the residential
                              property.
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {(loa.signatureImage || loa.status === 'signed') && (
                    <div>
                      <h3 className="text-lg font-medium mb-2">Signature</h3>
                      <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                        {loa.signatureImage ? (
                          <div className="max-w-xs">
                            <img
                              src={loa.signatureImage}
                              alt="Customer signature"
                              className="w-full h-auto max-h-40"
                            />
                            <div className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                              Signed on{' '}
                              {formatDate(loa.dateOfSigning || loa.updatedAt || loa.createdAt)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-slate-800 dark:text-slate-200">
                            Signature pending or not available
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {loa.status === 'draft' && loa.customerEmail && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 rounded-lg p-4">
                      <div className="flex items-start">
                        <div className="shrink-0 mt-0.5">
                          <svg
                            className="h-5 w-5 text-blue-600 dark:text-blue-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                            Ready to Send
                          </h3>
                          <div className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                            This Letter of Authority is ready to be sent to {loa.customerEmail} for
                            electronic signature.
                          </div>
                          <div className="mt-2">
                            <Button
                              variant="default"
                              size="sm"
                              className="gap-2"
                              onClick={() => sendLoaMutation.mutate()}
                              disabled={sendLoaMutation.isPending}
                            >
                              <Send className="h-4 w-4" />
                              {sendLoaMutation.isPending ? 'Sending...' : 'Send for Signature'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {loa.status === 'sent' && loa.customerEmail && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 rounded-lg p-4">
                      <div className="flex items-start">
                        <div className="shrink-0 mt-0.5">
                          <svg
                            className="h-5 w-5 text-amber-600 dark:text-amber-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">
                            Awaiting Signature
                          </h3>
                          <div className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                            This Letter of Authority was sent to {loa.customerEmail} on{' '}
                            {formatDate(loa.sentAt)}. Waiting for customer to sign.
                          </div>
                          <div className="mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={() => sendLoaMutation.mutate()}
                              disabled={sendLoaMutation.isPending}
                            >
                              <Send className="h-4 w-4" />
                              {sendLoaMutation.isPending ? 'Sending...' : 'Resend Email'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </PageLayout>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the Letter of Authority. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteLoaMutation.mutate()}
              disabled={deleteLoaMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoaMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DynamicMain>
  );
}
